import classNames from "classnames";
import React, { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../../../components/badge/Badge";
import IndividualContactSvg from "../../../components/svg/IndividualContactSvg";
import OrganizationIconSvg from "../../../components/svg/organizationIconSvg";
import { WorkDetailsModel } from "../../../factories/work/model/work-details.model";
import { useAppSelector } from "../../../redux/redux";
import { useUpdateWorkMutation } from "../../../slices/WorkSlice";
import { WorkButtons } from "../pages/details/widgets/work-main/WorkButtons";
import { RecurringBadge } from "../pages/repeats/RecurringIndicators";

type Props = {
  work: WorkDetailsModel;
};

export const WorkTitle: FC<Props> = ({ work }) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.appReducer);
  const [updateWork] = useUpdateWorkMutation();

  const [workName, setWorkName] = useState(work?.name || "");
  const [isEdit, setIsEdit] = useState(false);
  // Esc = leave without saving (the blur that follows must not save).
  const cancelEdit = useRef(false);

  const navigateToCustomer = () => {
    const isOrganization = !!work?.customer?.businessContactId;
    const path = isOrganization
      ? `/organization/${work?.customer?.businessContactId}`
      : `/contact/${work.customerId}?contactId=${work?.customer?.contactId}`;

    navigate(path);
  };

  const updateWorkName = () => {
    if (workName.trim() && workName !== work?.name) {
      updateWork({
        workId: work?.id,
        workName: workName,
        serviceId: work?.serviceId,
        taskTemplateId: work?.templateId,
        userId: work?.assignedUserId,
        customerId: work?.customerId,
        businessContactId: work?.customer?.businessContactId,
        isReassigned: work?.isReassigned,
        assignedByUserId: work?.assignedByUserId || user.id,
      });
    }
  };

  return (
    <div className={"flex w-full items-center justify-between"}>
      <div
        className={
          // Phones (QA batch 11): the name may take 2 lines and the client
          // goes under it; it was cut to "Claude…" at 390px.
          "flex min-w-0 items-center gap-3 text-2xl font-semibold text-gray-800 max-md:!flex-wrap max-md:gap-x-2 max-md:gap-y-1 max-md:text-xl"
        }>
        <div className={"flex min-w-0 items-center gap-2"}>
          {isEdit ? (
            <input
              type={"text"}
              className={"input input-bordered"}
              value={workName}
              onChange={(e) => setWorkName(e.target.value)}
              onBlur={() => {
                setIsEdit(false);
                if (cancelEdit.current) {
                  cancelEdit.current = false;
                  setWorkName(work?.name || "");
                  return;
                }
                updateWorkName();
              }}
              // Enter saves like clicking away; Esc keeps the old name (QA batch 8).
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  cancelEdit.current = true;
                  e.currentTarget.blur();
                }
              }}
              autoFocus
            />
          ) : (
            <span
              className={
                "min-w-0 cursor-pointer truncate font-notoSans hover:underline max-md:line-clamp-2 max-md:!whitespace-normal max-md:break-words"
              }
              onClick={() => {
                cancelEdit.current = false;
                setWorkName(work?.name || "");
                setIsEdit(true);
              }}>
              {work?.name}
            </span>
          )}
          {work.isArchived && <Badge label={"Archived"} variant={"archived"} />}
        </div>
        <div className={"h-5 w-px shrink-0 bg-[#D0D5DD] max-md:!hidden"} />
        {/* WITEM-002: the client stays on one line ("…") next to a long work name. */}
        <div
          className={
            "flex min-w-0 max-w-[40%] shrink-0 items-center gap-2 max-md:max-w-full"
          }>
          <div
            className={classNames(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
              work?.customer?.businessContactId
                ? "bg-[#581C87]"
                : "bg-[#002466]",
            )}>
            {work?.customer?.businessContactId ? (
              <OrganizationIconSvg className={"h-3.5 w-3.5"} />
            ) : (
              <IndividualContactSvg className={"h-3.5 w-3.5 text-white"} />
            )}
          </div>
          <span
            title={work?.customer?.name || undefined}
            className={
              "min-w-0 cursor-pointer truncate whitespace-nowrap py-0.5 font-notoSans text-sm leading-none hover:underline"
            }
            onClick={navigateToCustomer}>
            {work?.customer?.name}
          </span>
          <RecurringBadge work={work} />
        </div>
      </div>
      <div className={"shrink-0"}>
        <WorkButtons work={work} />
      </div>
    </div>
  );
};
